import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BookOpen, RefreshCcw } from 'lucide-react';
import api from '../../api';
import Alert from '../../components/Alert/Alert';
import Loading from '../../components/Loading/Loading';
import { groupCatalogByInputMode, modeLabel, modeTone } from './auditFormCatalogUtils';

const MODES = ['', 'structured_form', 'hybrid', 'upload_only', 'external_artifact'];
const VARIANTS = [
  { value: '', label: 'All Variants' },
  { value: 'ALL', label: 'Standard (ALL)' },
  { value: 'EU', label: 'EU' },
  { value: 'PLUS', label: 'PLUS' },
  { value: 'Japan-FIT', label: 'Japan-FIT' },
  { value: 'CORSIA', label: 'CORSIA' },
  { value: 'CORSIA_PLUS', label: 'CORSIA PLUS' },
];

const AuditFormCatalog = () => {
  const [serviceLine, setServiceLine] = useState('ISCC');
  const [inputMode, setInputMode] = useState('');
  const [variant, setVariant] = useState('');
  const [items, setItems] = useState([]);
  const [summaryByMode, setSummaryByMode] = useState({});
  const [summaryByVariant, setSummaryByVariant] = useState({});
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    setAlert(null);
    try {
      const params = { serviceLine };
      if (inputMode) params.inputMode = inputMode;
      if (variant) params.variant = variant;
      const response = await api.get('/audit/form-catalog', { params });
      setItems(Array.isArray(response.data?.items) ? response.data.items : []);
      setSummaryByMode(response.data?.summaryByInputMode || {});
      setSummaryByVariant(response.data?.summaryByVariant || {});
    } catch (error) {
      setItems([]);
      setSummaryByMode({});
      setSummaryByVariant({});
      setAlert({
        type: 'danger',
        message: error?.response?.data?.message || 'Unable to load audit form catalog.',
      });
    } finally {
      setLoading(false);
    }
  }, [serviceLine, inputMode, variant]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);


  const grouped = useMemo(() => groupCatalogByInputMode(items), [items]);
  const modeKeys = Object.keys(grouped);

  return (
    <div className="container-xl py-4">
      <div className="page-header d-print-none mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h2 className="page-title mb-1">Audit Form Catalog</h2>
            <div className="text-muted">ISCC/Sustainability document catalog with variant-aware input modes.</div>
          </div>
          <div className="col-auto d-flex gap-2 flex-wrap">
            <select className="form-select" value={serviceLine} onChange={(event) => setServiceLine(event.target.value)}>
              <option value="ISCC">ISCC / Sustainability</option>
            </select>
            <select className="form-select" value={variant} onChange={(event) => setVariant(event.target.value)}>
              {VARIANTS.map((v) => (
                <option key={v.value || 'all'} value={v.value}>{v.label}</option>
              ))}
            </select>
            <select className="form-select" value={inputMode} onChange={(event) => setInputMode(event.target.value)}>
              {MODES.map((mode) => (
                <option key={mode || 'all'} value={mode}>{mode ? modeLabel(mode) : 'All Modes'}</option>
              ))}
            </select>
            <button type="button" className="btn btn-outline-primary" onClick={fetchCatalog} disabled={loading} title="Refresh catalog">
              <RefreshCcw size={16} />
            </button>
          </div>
        </div>
      </div>

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="row g-3 mb-3">
        <div className="col-sm-6 col-xl-3">
          <div className="border rounded p-3 bg-white h-100">
            <div className="text-muted small">Total Items</div>
            <div className="h2 mb-0">{items.length}</div>
          </div>
        </div>
        {MODES.filter(Boolean).map((mode) => (
          <div className="col-sm-6 col-xl-3" key={mode}>
            <div className="border rounded p-3 bg-white h-100">
              <div className="text-muted small">{modeLabel(mode)}</div>
              <div className="h2 mb-0">{summaryByMode[mode] || 0}</div>
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <Loading />
      ) : modeKeys.length === 0 ? (
        <div className="empty">
          <div className="empty-icon"><BookOpen size={32} /></div>
          <p className="empty-title">No catalog items found</p>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {modeKeys.map((mode) => (
            <div className="card" key={mode}>
              <div className="card-header">
                <h3 className="card-title mb-0">{modeLabel(mode)}</h3>
              </div>
              <div className="table-responsive">
                <table className="table table-vcenter card-table">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Variant</th>
                      <th>Group</th>
                      <th>Role</th>
                      <th>Version</th>
                      <th>Status</th>
                      <th>Upload</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[mode].map((item) => (
                      <tr key={`${item.documentCode}-${item.variant || 'ALL'}`}>
                        <td>
                          <div className="fw-semibold">{item.documentCode}</div>
                          <div className="text-muted">{item.documentName}</div>
                        </td>
                        <td><span className="badge bg-blue-lt">{item.variant || 'ALL'}</span></td>
                        <td>{item.catalogGroup || '-'}</td>
                        <td>{item.requiredRole || '-'}</td>
                        <td>{item.version || '-'}</td>
                        <td><span className={`badge bg-${modeTone(mode)}-lt`}>{item.status || 'active'}</span></td>
                        <td>{item.uploadRequired ? 'Required' : 'Optional'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuditFormCatalog;
